import React, { FC } from 'react';
import { Navigate, useMatch, useParams } from 'react-router-dom';
import { Unstable_Grid2 as Grid } from '@mui/material';

import {
  ContentApprovalTypes,
  FeedKey,
  GroupContentApprovalType,
  GroupFragment,
  SearchContentQueryVariables,
  SearchContentSortFields,
  SortDirections,
  SortMode,
} from 'api';
import { FeedViewOptions } from 'types/app';
import { Layouts, LoaderComponents, Loaders, PaddedPaper, Queries } from 'components';
import {
  useCapabilities,
  useFollowed,
  useProtectedClient,
  useReleases,
  useUserPreferenceStorage,
} from 'hooks';
import {
  DEFAULT_SORT,
  FEED_LIMIT_LIST,
  LOCAL_STORAGE_KEY_FEED_SORT,
  LOCAL_STORAGE_KEY_FEED_VIEW,
} from 'app-constants';
import * as paths from 'paths';

import {
  FeedMenu,
  GroupMembers,
  GroupModeratorTabs,
  GroupPosts,
} from 'features/groups/components/View';
import { SetupProfileButton, SetupWorkspaceButton } from 'features/setup';
import { FeedSelect } from 'features/feed';

const ViewGroup: FC = () => {
  const client = useProtectedClient();
  const { groups } = useFollowed();
  const { needs } = useCapabilities();
  const params = useParams<{ group_id: string }>();
  const releases = useReleases();

  const has4EyeModeration = releases.includes('4EyeModeration');

  const [sortChoice, setSortChoice] = useUserPreferenceStorage(
    LOCAL_STORAGE_KEY_FEED_SORT,
    DEFAULT_SORT
  );

  const [view, setView] = useUserPreferenceStorage(
    LOCAL_STORAGE_KEY_FEED_VIEW,
    FeedViewOptions.List
  );

  const sort = client.sort_mode ? client.sort_mode === SortMode.Trending : sortChoice;
  const allowSort = client.sort_mode === null;

  const isPosts = !!useMatch(paths.group);
  const isMembers = !!useMatch(paths.groupMembers);
  const isApproval = !!useMatch(paths.groupApproval);
  const isScheduled = !!useMatch(paths.groupScheduled);
  const isRemoved = !!useMatch(paths.groupRemoved);

  const { group_id } = params;
  const isFollowing = groups.some(group => group.id === group_id);

  function handleSort() {
    setSortChoice(!sort);
  }

  function handleViewChange() {
    setView(view === FeedViewOptions.List ? FeedViewOptions.Grid : FeedViewOptions.List);
  }

  function renderMenu() {
    const selectedGroup = groups.find(group => group.id === group_id);

    if (!selectedGroup) {
      return null;
    }

    return (
      <FeedMenu
        selectedGroup={selectedGroup}
        onSort={handleSort}
        sort={sort}
        allowSort={allowSort}
      />
    );
  }

  function canModerate(group: GroupFragment, capability: string) {
    return needs(`Group.View.${capability}`, group.id);
  }

  function renderGroup(group: GroupFragment) {
    const variables = {
      sort,
      key: FeedKey.Group,
      id: group.id,
      limit: FEED_LIMIT_LIST,
    };

    const searchContentVariables: SearchContentQueryVariables = {
      search_content: {
        approval: ContentApprovalTypes.Submitted,
        group_list: [group.id],
        sort_by: [
          {
            direction: SortDirections.Desc,
            field: SearchContentSortFields.CreatedAt,
          },
        ],
      },
    };

    if (isPosts) {
      return (
        <GroupPosts
          activeView={view}
          onViewChange={handleViewChange}
          variables={variables}
          isFollowing={isFollowing}
          group={group}
        />
      );
    }

    if (isMembers) {
      return <GroupMembers isFollowing={isFollowing} group={group} />;
    }

    if (
      canModerate(group, 'Approval') &&
      isApproval &&
      (group.content_approval_type === GroupContentApprovalType.Review ||
        (has4EyeModeration && group.content_approval_type === GroupContentApprovalType.FourEyes))
    ) {
      return <GroupModeratorTabs group={group} variables={searchContentVariables} />;
    }

    if (canModerate(group, 'Scheduled') && isScheduled) {
      searchContentVariables.search_content.approval = ContentApprovalTypes.Scheduled;
      return <GroupModeratorTabs group={group} variables={searchContentVariables} />;
    }

    if (canModerate(group, 'Removed') && isRemoved) {
      searchContentVariables.search_content.approval = ContentApprovalTypes.Removed;
      return <GroupModeratorTabs group={group} variables={searchContentVariables} />;
    }

    return <Navigate to={paths.groups} replace />;
  }

  function renderLoader() {
    return (
      <Layouts.AppMain2Column
        desktopSplit="8/4"
        header={
          <PaddedPaper padding="16px">
            <Grid container spacing={2}>
              <Grid>
                <LoaderComponents.Image shape="rounded" height={88} width={60} />
              </Grid>
              <Grid>
                <LoaderComponents.Block width={300} height={88} />
              </Grid>
            </Grid>
          </PaddedPaper>
        }
        left={<Loaders.Feed />}
        right={<Loaders.Feed />}
      />
    );
  }

  return (
    <Layouts.App2Column
      mainWidth={view === FeedViewOptions.Grid ? 'wide' : 'standard'}
      sidebar={
        <FeedSelect menu={renderMenu()}>
          <SetupWorkspaceButton />
          <SetupProfileButton />
        </FeedSelect>
      }
      main={
        <Queries.GetGroup loader={renderLoader()} variables={{ id: group_id }}>
          {group => {
            if (!group) {
              return <Navigate to={paths.groups} replace />;
            }

            return renderGroup(group);
          }}
        </Queries.GetGroup>
      }
    />
  );
};

export default ViewGroup;
